import { HttpClient } from '@angular/common/http'
import { inject, Injectable } from '@angular/core'
import { auditTime, BehaviorSubject, firstValueFrom, map, Observable, Subject } from 'rxjs'

import { LoggerService } from '../../logger.service'
import { ToastService } from '../../toast.service'
import { anchorKey, EditorThread, EditorThreadAnchorType, EditorThreadComment } from './editor-thread.types'

const SSE_REFETCH_DEBOUNCE_MS = 250

type PostCommentResponse = {
  thread: { id: string; anchorType: EditorThreadAnchorType; anchorId: string }
  comment: EditorThreadComment
}

type CurrentUserInfo = {
  id: number
  name: string
}

type PostCommentParams = {
  callId: number
  anchorType: EditorThreadAnchorType
  anchorId: string
  body: string
  currentUser: CurrentUserInfo
}

function cloneThread(thread: EditorThread): EditorThread {
  return { ...thread, comments: [...thread.comments] }
}

function buildOptimisticComment(threadId: string, user: CurrentUserInfo, body: string): EditorThreadComment {
  return {
    id: `optimistic-${crypto.randomUUID()}`,
    threadId,
    userId: user.id,
    userName: user.name,
    body,
    createdAt: new Date().toISOString(),
  }
}

@Injectable({ providedIn: 'root' })
export class EditorThreadsService {
  private http = inject(HttpClient)
  private logger = inject(LoggerService)
  private toastService = inject(ToastService)

  private threadsSubject = new BehaviorSubject<Map<string, EditorThread>>(new Map())
  private refetchSubject = new Subject<number>()
  private loadAllToken = 0

  readonly threads$: Observable<Map<string, EditorThread>> = this.threadsSubject.asObservable()

  constructor() {
    this.refetchSubject.pipe(auditTime(SSE_REFETCH_DEBOUNCE_MS)).subscribe((callId) => {
      void this.loadAll(callId)
    })
  }

  threadFor(anchorType: EditorThreadAnchorType, anchorId: string): EditorThread | null {
    return this.threadsSubject.value.get(anchorKey(anchorType, anchorId)) ?? null
  }

  threadFor$(anchorType: EditorThreadAnchorType, anchorId: string): Observable<EditorThread | null> {
    const key = anchorKey(anchorType, anchorId)
    return this.threads$.pipe(map((threads) => threads.get(key) ?? null))
  }

  async loadAll(callId: number): Promise<void> {
    const token = ++this.loadAllToken
    try {
      const threads = await firstValueFrom(this.http.get<EditorThread[]>(`/api/tiptap-editor/calls/${callId}/threads`))
      if (token !== this.loadAllToken) {
        return
      }
      const next = new Map<string, EditorThread>()
      for (const thread of threads) {
        next.set(anchorKey(thread.anchorType, thread.anchorId), thread)
      }
      this.threadsSubject.next(next)
    } catch (error) {
      this.logger.error('Failed to load editor threads', error)
    }
  }

  async postComment(params: PostCommentParams): Promise<void> {
    const trimmed = params.body.trim()
    if (trimmed.length === 0) {
      return
    }

    const { callId, anchorType, anchorId, currentUser } = params
    const key = anchorKey(anchorType, anchorId)
    const existing = this.threadsSubject.value.get(key) ?? null
    const synthesizedThreadId = existing?.id ?? `optimistic-thread-${crypto.randomUUID()}`
    const optimisticComment = buildOptimisticComment(synthesizedThreadId, currentUser, trimmed)
    const optimisticThread: EditorThread = {
      id: synthesizedThreadId,
      anchorType,
      anchorId,
      comments: existing === null ? [optimisticComment] : [...existing.comments, optimisticComment],
    }

    this.setThread(key, optimisticThread)

    try {
      const response = await firstValueFrom(
        this.http.post<PostCommentResponse>(`/api/tiptap-editor/calls/${callId}/thread-comments`, {
          anchorType,
          anchorId,
          body: trimmed,
        })
      )

      const currentMap = this.threadsSubject.value
      const currentThread = currentMap.get(key) ?? null
      const replacedComments = (currentThread?.comments ?? []).map((comment) => (comment.id === optimisticComment.id ? response.comment : comment))
      const persistedComments = replacedComments.some((comment) => comment.id === response.comment.id)
        ? replacedComments
        : [...replacedComments, response.comment]

      this.setThread(key, {
        id: response.thread.id,
        anchorType: response.thread.anchorType,
        anchorId: response.thread.anchorId,
        comments: persistedComments,
      })
    } catch (error) {
      const rollback = new Map(this.threadsSubject.value)
      if (existing === null) {
        rollback.delete(key)
      } else {
        rollback.set(key, existing)
      }
      this.threadsSubject.next(rollback)
      this.logger.error('Failed to post editor thread comment', error)
      this.toastService.show('EDITOR.TRACK_CHANGES.SIDEBAR.COMMENT_SUBMIT_ERROR')
      throw error
    }
  }

  async archiveThread(callId: number, anchorType: EditorThreadAnchorType, anchorId: string): Promise<void> {
    const key = anchorKey(anchorType, anchorId)
    const removed = this.threadsSubject.value.get(key) ?? null
    if (removed !== null) {
      const next = new Map(this.threadsSubject.value)
      next.delete(key)
      this.threadsSubject.next(next)
    }

    try {
      await firstValueFrom(this.http.post<void>(`/api/tiptap-editor/calls/${callId}/threads/archive`, { anchorType, anchorId }))
    } catch (error) {
      if (removed !== null && !this.threadsSubject.value.has(key)) {
        const rollback = new Map(this.threadsSubject.value)
        rollback.set(key, removed)
        this.threadsSubject.next(rollback)
      }
      this.logger.error('Failed to archive editor thread', error)
      // The caller drops the shared anchor once this resolves, so a refused archive has to
      // surface — otherwise the anchor goes and the thread stays behind on the server.
      throw error
    }
  }

  requestRefetch(callId: number) {
    this.refetchSubject.next(callId)
  }

  reset() {
    this.loadAllToken++
    this.threadsSubject.next(new Map())
  }

  private setThread(key: string, thread: EditorThread) {
    const next = new Map(this.threadsSubject.value)
    next.set(key, cloneThread(thread))
    this.threadsSubject.next(next)
  }
}
